import { usePreviewUpdateStore } from "@/store/zustand/previewUpdateStore";
import type { JsxElement } from "./jsx/jsxElementTypes";

const uploadPreviewToStorage = async (
  componentKey: string,
  imageDataUrl: string,
  projectId: string,
  supabaseJWT: string,
) => {
  try {
    // Convert data URL to blob for cloud upload
    const response = await fetch(imageDataUrl);
    const blob = await response.blob();

    // Since we're only capturing JPEG now, use fixed values
    const extension = "jpeg";
    const mimeType = "image/jpeg";

    // Use the componentKey as-is for the filename
    // This should match what generatePreviewKey returns for the same element
    const componentPreviewName = `${componentKey}.${extension}`;

    // Create a file object from the blob with correct type
    const file = new File([blob], componentPreviewName, {
      type: mimeType,
    });

    // Convert file to base64 for API upload
    const reader = new FileReader();
    const base64Promise = new Promise<string>((resolve, reject) => {
      reader.onload = () => {
        const result = reader.result as string;
        const base64Data = result.split(",")[1];
        resolve(base64Data);
      };
      reader.onerror = () => reject(new Error("Failed to read file"));
    });

    reader.readAsDataURL(file);
    const base64Data = await base64Promise;

    // Create nested folder path with projectId to match expected URL structure
    const folderPath = `component-previews/${projectId}`;

    // Upload to Google Cloud Storage
    const uploadResponse = await fetch(
      `${import.meta.env.VITE_BACKEND_BASE_URL}/imageUpload`,
      {
        method: "POST",
        body: JSON.stringify({
          file: base64Data,
          fileName: componentPreviewName,
          folderPath: folderPath,
          mimeType: mimeType,
          makePublic: true,
        }),
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${supabaseJWT}`,
        },
      },
    );

    if (!uploadResponse.ok) {
      throw new Error(`Upload failed: ${uploadResponse.statusText}`);
    }

    const uploadData = await uploadResponse.json();

    if (uploadData.error) {
      throw new Error(uploadData.error);
    }

    return uploadData.url;
  } catch (error) {
    console.error("[PREVIEW UPLOAD] Upload error:", error);
    return null;
  }
};

export const handlePreviewCaptured = (
  componentKey: string,
  imageDataUrl: string,
  projectId: string,
  supabaseJWT: string,
) => {
  uploadPreviewToStorage(
    componentKey,
    imageDataUrl,
    projectId,
    supabaseJWT,
  ).then((previewUrl) => {
    if (previewUrl) {
      // Notify preview update with multiple possible key formats to ensure detection
      const store = usePreviewUpdateStore.getState();

      // Always notify with the original componentKey
      store.notifyPreviewUpdate(componentKey);

      // Extract base component ID and variant name if possible
      const keyParts = componentKey.split("_");
      if (keyParts.length >= 2) {
        const baseComponentId = keyParts[0];
        const variantName = keyParts.slice(1).join("_");

        // Also notify with just the base component ID
        store.notifyPreviewUpdate(baseComponentId);

        // And with the full componentKey format that might be used elsewhere
        if (variantName && variantName !== baseComponentId) {
          store.notifyPreviewUpdate(`${baseComponentId}_${variantName}`);
        }
      }
    }
  });
};

export const handlePreviewError = (componentKey: string, error: string) => {
  console.error(
    `[PREVIEW UPLOAD] Failed to generate preview for ${componentKey}:`,
    error,
  );
};

/**
 * Constructs the public preview URL from a preview key
 */
export const getPreviewUrl = (
  previewKey: string,
  projectId: string,
): string => {
  return `https://storage.googleapis.com/tempo-image-previews/component-previews/${projectId}/${previewKey}.jpeg`;
};

/**
 * Test if an image URL is accessible
 */

export const testImageUrl = (url: string): Promise<boolean> => {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve(true);
    img.onerror = () => resolve(false);
    setTimeout(() => resolve(false), 10000);
    img.src = url;
  });
};

/**
 * Get the preview URL for an element using the same priority logic as useMetadataForElements
 * Returns the first possible URL (for compatibility with existing code)
 */

export const getPreviewUrlForElement = (
  element: JsxElement,
  projectId?: string,
): string | null => {
  if (!element.fileHash || !projectId) {
    return null;
  }
  return getPreviewUrl(element.fileHash, projectId);
};
/**
 * Check if a preview exists for an element (async version for background processing)
 */

export const checkPreviewExists = async (
  element: JsxElement,
  projectId: string,
): Promise<boolean> => {
  // Get all possible preview URLs using the centralized logic
  const url = getPreviewUrlForElement(element, projectId);

  if (!url) {
    return false;
  }

  // Test each URL in order until we find one that works
  try {
    const isValid = await testImageUrl(url);
    if (isValid) {
      return true;
    }
  } catch (error) {
    // Continue to next URL
  }

  return false;
};
